import { useCallback, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { getPrizePoolCurrentUrl } from '../../api/generated/game-punch/game-punch.ts';
import CoinImage from '../../assets/icons/punch/coin.png';

const getMockPotIncrement = () => {
  const roll = Math.random();
  if (roll < 0.7) return 1 + Math.floor(Math.random() * 4);
  if (roll < 0.95) return 5 + Math.floor(Math.random() * 16);
  return 25 + Math.floor(Math.random() * 36);
};

const formatSeasonDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}.${mm}.${dd}`;
};

function PunchPrizeSummaryView({ summary, isBurningActive = false }) {
  const [displayPotPoint, setDisplayPotPoint] = useState(summary.potPoint);
  const [potSettleClass, setPotSettleClass] = useState('');
  const displayPotPointRef = useRef(summary.potPoint);

  useEffect(() => {
    displayPotPointRef.current = displayPotPoint;
  }, [displayPotPoint]);

  useEffect(() => {
    if (displayPotPointRef.current === summary.potPoint) return undefined;

    const target = summary.potPoint;
    const start = displayPotPointRef.current;
    const totalTicks = 10;
    let tick = 0;
    let settleTimer;

    const timer = window.setInterval(() => {
      tick += 1;
      if (tick >= totalTicks) {
        window.clearInterval(timer);
        setDisplayPotPoint(target);
        // setPotSettleClass('punch-pot-settle');
        // settleTimer = window.setTimeout(() => {
        //   setPotSettleClass('');
        // }, 280);
        return;
      }

      const distance = Math.abs(target - start);
      const remainingRatio = 1 - tick / totalTicks;
      const noise = Math.max(40, Math.floor(distance * remainingRatio * 0.55));
      const randomOffset = Math.floor(Math.random() * (noise * 2 + 1)) - noise;
      const towardTarget = Math.floor((target - start) * (tick / totalTicks));
      const nextValue = target + randomOffset + towardTarget;
      setDisplayPotPoint(Math.max(0, nextValue));
    }, 42);

    return () => {
      window.clearInterval(timer);
      if (settleTimer) window.clearTimeout(settleTimer);
    };
  }, [summary.potPoint]);

  return (
    <section
      className="relative z-[3] mt-5 flex w-[276px] flex-col items-center gap-[10px]"
      aria-label="회차 상금 정보"
    >
      <div className="flex flex-col items-center">
        <div className="flex items-center gap-[2px]">
          <img src={CoinImage} alt="코인" className="h-[20px] w-[20px] object-cover" />
          <p
            className={`m-0 font-['Pretendard'] text-xs font-bold leading-[18px] tracking-[-0.3px] ${
              isBurningActive ? 'text-[#FFDCC7]' : 'text-gray04'
            }`}
          >
            이번회차 상금
          </p>
        </div>
        <div className="flex items-end gap-[4px]">
          <p
            className={`m-0 font-['Pretendard'] text-[36px] font-extrabold leading-none tracking-[-1.08px] ${potSettleClass} ${
              isBurningActive ? 'text-white' : 'text-black'
            }`}
          >
            {displayPotPoint.toLocaleString()}
          </p>
          <div className="flex items-center justify-center py-[5px]">
            <span
              className={`font-['Pretendard'] text-[15px] font-bold leading-none ${
                isBurningActive ? 'text-[#FFE8D9]' : 'text-gray05'
              }`}
            >
              P
            </span>
          </div>
        </div>
      </div>
      <p
        className={`m-0 text-center font-['Pretendard'] text-xs font-bold leading-[18px] tracking-[-0.3px] ${
          isBurningActive ? 'text-[#FFE8D9]' : 'text-gray05'
        }`}
      >
        <span className={isBurningActive ? 'text-[#FFD54F]' : 'text-primary-purple-01'}>{summary.roundLabel}</span>{' '}
        {summary.periodLabel}
      </p>
    </section>
  );
}

export default function PunchPrizePoolSummary({
  isBurningActive,
  isPunchMockModeEnabled,
  summary,
  stateMeta,
  livePoolAmountFromPlay,
  accessToken,
  API_SERVER_DOMAIN,
  fetchWithToken,
}) {
  const [mockPotPoint, setMockPotPoint] = useState(summary.potPoint);

  const fetchPrizePoolCurrent = useCallback(
    async (...args) => {
      try {
        const response = await fetchWithToken(...args);
        return response;
      } catch (error) {
        console.log('backend api: error GET /punch/prize-pool/current', { error });
        throw error;
      }
    },
    [fetchWithToken],
  );

  const { data: prizePoolCurrentData } = useSWR(
    accessToken && !isPunchMockModeEnabled && [`${API_SERVER_DOMAIN}${getPrizePoolCurrentUrl()}`, accessToken],
    fetchPrizePoolCurrent,
    { refreshInterval: 3000 },
  );

  useEffect(() => {
    if (!isPunchMockModeEnabled) return undefined;

    let timerId;
    let isActive = true;

    const scheduleNext = () => {
      const delayMs = 900 + Math.floor(Math.random() * 2700);
      timerId = window.setTimeout(() => {
        setMockPotPoint((prev) => prev + getMockPotIncrement());
        if (isActive) scheduleNext();
      }, delayMs);
    };

    scheduleNext();
    return () => {
      isActive = false;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [isPunchMockModeEnabled]);

  const livePoolAmount = Number(prizePoolCurrentData?.poolAmount);
  const immediatePoolAmount = Number(livePoolAmountFromPlay);
  const resolvedLivePoolAmount = Number.isFinite(livePoolAmount) && Number.isFinite(immediatePoolAmount)
    ? Math.max(livePoolAmount, immediatePoolAmount)
    : Number.isFinite(immediatePoolAmount)
      ? immediatePoolAmount
      : livePoolAmount;
  const liveSeasonNo = stateMeta?.seasonNo;
  const liveSeasonStartDate = formatSeasonDate(stateMeta?.seasonStartDate);
  const liveSeasonEndDate = formatSeasonDate(stateMeta?.seasonEndDate);
  const displaySummary = {
    ...summary,
    potPoint: !isPunchMockModeEnabled && Number.isFinite(resolvedLivePoolAmount) ? resolvedLivePoolAmount : mockPotPoint,
    roundLabel: !isPunchMockModeEnabled && Number.isFinite(liveSeasonNo) ? `${liveSeasonNo}회차` : summary.roundLabel,
    periodLabel:
      !isPunchMockModeEnabled && liveSeasonStartDate && liveSeasonEndDate
        ? `${liveSeasonStartDate} ~ ${liveSeasonEndDate}`
        : summary.periodLabel,
  };

  return <PunchPrizeSummaryView summary={displaySummary} isBurningActive={isBurningActive} />;
}
